import { useState, useEffect, useCallback, useRef } from 'react';
import { toCamelCase } from '../utils/caseTransformers';
import { useAuth } from '../context/AuthContext'; // Assuming this is your AuthContext hook
import { calculateUnfoldedDimensions, calculateFoldedDimensions } from '../components/small-format/foldingUtils';
import { isFinishAvailable } from '../components/small-format/finishingAvailability';

const COLOUR_PRINTER_ID = "colour";
const MONO_PRINTER_ID = "mono";
const LONG_FEED_PRINTER_ID = "colour-long-feed";
const FOLD_ID = "fold";
const CREASE_ID = "crease";
const BOOKLET_JOB_TYPES = ['booklet', 'wirebound-books', 'perfect-bound-books', 'brochure'];
const CUSTOM_JOB_TYPE_ID = 'custom/bespoke';

const BINDING_TYPE_TO_FINISH_ID_MAP = {
  'saddle-stitch': 'saddle-stitch',
  'wire_bound': 'wirebind',
  'perfect_bound': 'perfect-bind'
};
const ALL_BINDING_FINISH_IDS = Object.values(BINDING_TYPE_TO_FINISH_ID_MAP);

// New mapping for binding type to job type
const BINDING_TYPE_TO_JOB_TYPE_MAP = {
  'saddle-stitch': 'booklet',
  'wire_bound': 'wirebound-books',
  'perfect_bound': 'perfect-bound-books'
};

export const useSmallFormatFormLogic = ({
  apiBaseUrl,
  initialJobType = '',
  initialPaperStock = '',
  initialWidth = 0,
  initialHeight = 0,
  initialQuantity = 1,
  pricingTier, // NEW: Allow override of pricing tier from manager interface
  selectedCustomer = null, // NEW: For custom pricing in manager mode
  // Pass data from useSmallFormatData hook
  jobTypes = [],
  paperStocks = [],
  finishingProcesses = [],
  printers = [],
  miscCosts = [],
  paperSizes = {},
  finishingMaterials = {},
  // For quote loading protection
  isLoadingQuote,
  jobTypeLoadedFromQuoteRef,
  fieldsLoadedFromQuote,
}) => {
  const API_URL = apiBaseUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { currentUser } = useAuth();

  // Form inputs state
  const [jobType, setJobType] = useState(initialJobType);
  const [paperStock, setPaperStock] = useState(initialPaperStock);
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [selectedFinishes, setSelectedFinishes] = useState([]);
  const [selectedMiscCosts, setSelectedMiscCosts] = useState([]);
  const [printer, setPrinter] = useState('');
  const [standardSize, setStandardSize] = useState('');
  const [isDoubleSided, setIsDoubleSided] = useState(true);
  const [colours, setColours] = useState('full_colour');
  const [notes, setNotes] = useState('');
  const [bleed, setBleed] = useState(3);
  const [numberOfDesigns, setNumberOfDesigns] = useState(1);
  const [jobName, setJobName] = useState('');
  const [finishingOptions, setFinishingOptions] = useState({});

  // Booklet-specific state
  const [isBooklet, setIsBooklet] = useState(false);
  const [pageCount, setPageCount] = useState(16);
  const [coverStock, setCoverStock] = useState('');
  const [separateCover, setSeparateCover] = useState(false);
  const [coverColours, setCoverColours] = useState('full_colour');
  const [coverIsDoubleSided, setCoverIsDoubleSided] = useState(true);
  const [bindingType, setBindingType] = useState('saddle-stitch');
  const [selectedCoverFinishes, setSelectedCoverFinishes] = useState([]);
  const [coverFinishingOptions, setCoverFinishingOptions] = useState({});

  // Special options state
  const [foldType, setFoldType] = useState('');
  const [isFolded, setIsFolded] = useState(false);
  const [dimensionInputType, setDimensionInputType] = useState('finished');

  // NCR-specific state
  const [ncrType, setNcrType] = useState('sets');
  const [sheetsPerSet, setSheetsPerSet] = useState(2);
  const [setsPerPad, setSetsPerPad] = useState(50);
  const [paperSelections, setPaperSelections] = useState([]);
  const [hasWraparoundShield, setHasWraparoundShield] = useState(false);
  const [padGlueEdge, setPadGlueEdge] = useState('top');
  const [hasNumbering, setHasNumbering] = useState(false);
  const [numberingStartAt, setNumberingStartAt] = useState(1);
  const [hasLooseShield, setHasLooseShield] = useState(false);
  const [sheetPrinters, setSheetPrinters] = useState([]); // Array of printer IDs per sheet (one per sheets_per_set)
  const [sheetSides, setSheetSides] = useState([]); // Array of sides per sheet (1 or 2 for each sheet)
  // New state for 'pads' (non-NCR) job type
  const [padJobSheetsPerPad, setPadJobSheetsPerPad] = useState(50);
  // padGlueEdge is already defined and can be reused for 'pads' job type
  const [isSettingFromQuoteInternal, setIsSettingFromQuoteInternal] = useState(false); // Internal state for quote loading

  // Track user-modified fields to prevent suggestion overrides
  const [userModifiedFields, setUserModifiedFields] = useState(new Set());
  // Calculation result state
  const [calculatedPrice, setCalculatedPrice] = useState(0);

  // Ref to prevent job suggestion loading on internal binding type changes
  const isInternalJobTypeChangeRef = useRef(false);
  const [breakdown, setBreakdown] = useState({});
  const [impositionDetails, setImpositionDetails] = useState(null);
  const [bookletDetails, setBookletDetails] = useState(null);
  const [foldingDetails, setFoldingDetails] = useState(null);
  const [paperUsage, setPaperUsage] = useState(null);

  // Error states
  const [calculationError, setCalculationError] = useState(null);
  const [detailsError, setDetailsError] = useState(null);

  // Helper function to update state for dropdowns/radios
  const handleOptionChange = (processId, typeKey, materialId) => {
    setFinishingOptions(prev => {
      const currentMaterialId = prev[processId]?.[typeKey];
      const newMaterialId = materialId || null;

      if (currentMaterialId === newMaterialId) {
        return prev; // No change, return previous state to prevent re-render
      }

      const newOptions = {
        ...prev,
        [processId]: {
          ...prev[processId],
          [typeKey]: newMaterialId
        }
      };
      return newOptions;
    });
  };

  // Helper function to handle checkbox changes (maps checked state to MaterialID)
  const handleCheckboxChange = (processId, typeKey, material, isChecked) => {
    handleOptionChange(processId, typeKey, isChecked ? material.material_id : null);
  };

  // Helper function for cover finish options
  const handleCoverOptionChange = (processId, typeKey, materialId) => {
    setCoverFinishingOptions(prev => ({
      ...prev,
      [processId]: {
        ...prev[processId],
        [typeKey]: materialId || null
      }
    }));
  };

  // Helper function for cover finish checkbox changes
  const handleCoverCheckboxChange = (processId, typeKey, material, isChecked) => {
    handleCoverOptionChange(processId, typeKey, isChecked ? material.material_id : null);
  };

  // Detect sheet type based on dimensions (front-end validation)
  const detectSheetType = useCallback((w, h, isBookletJob, binding, bleedValue) => {
    // Determine imposition dimensions based on job type
    let impositionWidth = w;
    let impositionHeight = h;

    // For saddle-stitch booklets, use spread dimensions
    if (isBookletJob && binding === 'saddle-stitch') {
      impositionWidth = w * 2;  // Spread = page width × 2
      impositionHeight = h;
    }
    // For wire/perfect bound or flat items, use dimensions as-is

    // Constants (match backend exactly)
    const SRA3_WIDTH = 320;
    const SRA3_HEIGHT = 450;
    const LONG_SHEET_WIDTH = 320;
    const LONG_SHEET_HEIGHT = 640;
    const DEFAULT_MARGIN = 0;  // No margin - bleed accounts for space

    // Add bleed
    const itemWithBleed_W = impositionWidth + (bleedValue * 2);
    const itemWithBleed_H = impositionHeight + (bleedValue * 2);

    // Check SRA3 (both orientations)
    const usableSRA3_W = SRA3_WIDTH - (DEFAULT_MARGIN * 2);
    const usableSRA3_H = SRA3_HEIGHT - (DEFAULT_MARGIN * 2);

    const fitsSRA3_Portrait = (itemWithBleed_W <= usableSRA3_W && itemWithBleed_H <= usableSRA3_H);
    const fitsSRA3_Landscape = (itemWithBleed_W <= usableSRA3_H && itemWithBleed_H <= usableSRA3_W);

    if (fitsSRA3_Portrait || fitsSRA3_Landscape) {
      return 'SRA3';
    }

    // Check long sheet (both orientations)
    const usableLong_W = LONG_SHEET_WIDTH - (DEFAULT_MARGIN * 2);
    const usableLong_H = LONG_SHEET_HEIGHT - (DEFAULT_MARGIN * 2);

    const fitsLong_Portrait = (itemWithBleed_W <= usableLong_W && itemWithBleed_H <= usableLong_H);
    const fitsLong_Landscape = (itemWithBleed_W <= usableLong_H && itemWithBleed_H <= usableLong_W);

    if (fitsLong_Portrait || fitsLong_Landscape) {
      return 'LONG_SHEET';
    }

    return 'TOO_LARGE';
  }, []);

  // --- Effects ---
  // Handle printer selection based on dimensions and colours (long sheet detection)
  // This replaces the old simple colour-based selection
  useEffect(() => {
    if (!printers || printers.length === 0) {
      return; // Wait until printers data is available
    }

    // Detect required sheet type (works for all job types)
    const sheetType = detectSheetType(width, height, isBooklet, bindingType, bleed);

    // Select appropriate printer
    if (sheetType === 'LONG_SHEET') {
      // Long sheet always uses colour_long_feed (handles both colour & B&W)
      if (printers.some(p => p.id === LONG_FEED_PRINTER_ID)) {
        setPrinter(LONG_FEED_PRINTER_ID);
      } else {
        console.warn(`Long-feed printer "${LONG_FEED_PRINTER_ID}" not found.`);
        setPrinter('');
      }
    } else if (sheetType === 'SRA3') {
      // Standard SRA3 printing - use colour or mono based on colours selection
      if (colours === 'full_colour') {
        if (printers.some(p => p.id === COLOUR_PRINTER_ID)) {
          setPrinter(COLOUR_PRINTER_ID);
        } else {
          console.warn(`Colour printer "${COLOUR_PRINTER_ID}" not found.`);
          setPrinter('');
        }
      } else if (colours === 'black_white') {
        if (printers.some(p => p.id === MONO_PRINTER_ID)) {
          setPrinter(MONO_PRINTER_ID);
        } else {
          console.warn(`Mono printer "${MONO_PRINTER_ID}" not found.`);
          setPrinter('');
        }
      }
    }
    // If TOO_LARGE, backend will return error during calculation
  }, [colours, width, height, isBooklet, bindingType, bleed, detectSheetType, printers]);

  useEffect(() => {
    // Skip if currently loading from a quote, or if not an NCR job, or if essential data is missing
    if (isSettingFromQuoteInternal || jobType !== 'ncr' || sheetsPerSet <= 0 || paperStocks.length <= 0) {
      return;
    }
    // Only proceed with auto-selection if current paper selections are incomplete or invalid
    if (paperSelections.length !== sheetsPerSet || paperSelections.some(s => !s)) {
      const topPapers = paperStocks.filter(s => s.category?.toLowerCase().includes('ncr') && s.ncr_layer === 'top');
      const middlePapers = paperStocks.filter(s => s.category?.toLowerCase().includes('ncr') && s.ncr_layer === 'middle');
      const bottomPapers = paperStocks.filter(s => s.category?.toLowerCase().includes('ncr') && s.ncr_layer === 'bottom');
      const newSelections = [];
      if (sheetsPerSet >= 1 && topPapers.length > 0) newSelections.push(topPapers.find(p => p.name?.toLowerCase().includes('white'))?.id || topPapers[0].id);
      for (let i = 1; i < sheetsPerSet - 1; i++) if (middlePapers.length > 0) newSelections.push(middlePapers[Math.min(i - 1, middlePapers.length - 1)].id);
      if (sheetsPerSet > 1 && bottomPapers.length > 0 && newSelections.length < sheetsPerSet) newSelections.push(bottomPapers[0].id);
      if (newSelections.length === sheetsPerSet) setPaperSelections(newSelections);
      else if (paperSelections.length !== 0) setPaperSelections([]);
    }
  }, [jobType, sheetsPerSet, paperStocks, paperSelections, isSettingFromQuoteInternal]); // Add isSettingFromQuoteInternal to dependencies

  useEffect(() => {
    setIsFolded(!!foldType);
  }, [foldType]);

  useEffect(() => {
    if (isLoadingQuote || isSettingFromQuoteInternal) return;

    const selectedPaper = paperStocks.find(stock => stock.id === paperStock);
    setSelectedFinishes(previous =>
      previous.filter(finishId => {
        const process = finishingProcesses.find(item => item.id === finishId);
        return !process || isFinishAvailable(process, selectedPaper, jobType);
      })
    );
  }, [jobType, paperStock, paperStocks, finishingProcesses, isLoadingQuote, isSettingFromQuoteInternal]);

  useEffect(() => {
    if (isLoadingQuote || isSettingFromQuoteInternal) return;

    const selectedCover = paperStocks.find(stock => stock.id === coverStock);
    setSelectedCoverFinishes(previous =>
      previous.filter(finishId => {
        const process = finishingProcesses.find(item => item.id === finishId);
        return !process || isFinishAvailable(process, selectedCover, jobType);
      })
    );
  }, [jobType, coverStock, paperStocks, finishingProcesses, isLoadingQuote, isSettingFromQuoteInternal]);

  useEffect(() => {
    if (isLoadingQuote) return;
    if (isSettingFromQuoteInternal || (jobTypeLoadedFromQuoteRef.current === jobType && !isLoadingQuote)) return;

    const isStandardBooklet = BOOKLET_JOB_TYPES.includes(jobType);
    if (jobType === CUSTOM_JOB_TYPE_ID) {
      // isBooklet is user-controlled for custom jobs
    } else {
      if (isBooklet !== isStandardBooklet) {
        setIsBooklet(isStandardBooklet);
        if (!isStandardBooklet) {
          setPageCount(16); setBindingType('saddle-stitch'); setSeparateCover(false);
          setCoverStock(''); setCoverColours('full_colour'); setSelectedCoverFinishes([]);
          setCoverFinishingOptions({});
        }
      }
    }
  }, [jobType, isBooklet, isLoadingQuote, jobTypeLoadedFromQuoteRef, isSettingFromQuoteInternal]);

  useEffect(() => {
    if (isLoadingQuote || isSettingFromQuoteInternal) return;
    const creaseProcess = finishingProcesses.find(p => p.id === CREASE_ID);
    if (!creaseProcess) return;

    const conditionsMet = isBooklet && bindingType === 'saddle-stitch' && separateCover && coverStock;
    if (conditionsMet) {
      const selectedCover = paperStocks.find(s => s.id === coverStock);
      if (selectedCover && selectedCover.weight_gsm >= 170) {
        // Move the duplicate check INSIDE the functional setter to see the most recent state
        setSelectedCoverFinishes(prev => {
          if (!prev.includes(CREASE_ID)) {
            return [...prev, CREASE_ID];
          }
          return prev; // Already has crease, don't add
        });
      }
    }
  }, [isBooklet, bindingType, separateCover, coverStock, paperStocks, finishingProcesses, selectedCoverFinishes, setSelectedCoverFinishes, isLoadingQuote, isSettingFromQuoteInternal]);

  // Automatic crease for inner pages on thick paper in saddle-stitched booklets
  useEffect(() => {
    if (isLoadingQuote || isSettingFromQuoteInternal) return;
    const creaseProcess = finishingProcesses.find(p => p.id === CREASE_ID);
    if (!creaseProcess) return;

    const conditionsMet = isBooklet && bindingType === 'saddle-stitch' && paperStock;
    if (conditionsMet) {
      const selectedInnerPaper = paperStocks.find(s => s.id === paperStock);
      if (selectedInnerPaper && selectedInnerPaper.weight_gsm >= 170) {
        // Move the duplicate check INSIDE the functional setter to see the most recent state
        setSelectedFinishes(prev => {
          if (!prev.includes(CREASE_ID)) {
            return [...prev, CREASE_ID];
          }
          return prev; // Already has crease, don't add
        });
      } else if (selectedInnerPaper && selectedInnerPaper.weight_gsm < 170) {
        // Remove automatic crease if paper is changed to lighter weight (preserve manual selections)
        setSelectedFinishes(prev => prev.filter(id => id !== CREASE_ID));
      }
    }
  }, [isBooklet, bindingType, paperStock, paperStocks, finishingProcesses, setSelectedFinishes, isLoadingQuote, isSettingFromQuoteInternal]);

  useEffect(() => {
    if (isLoadingQuote || isSettingFromQuoteInternal) return;
    if (isBooklet) {
      const targetFinishId = BINDING_TYPE_TO_FINISH_ID_MAP[bindingType];
      setSelectedFinishes(prev => {
        let newFinishes = prev.filter(id => !ALL_BINDING_FINISH_IDS.includes(id));
        if (targetFinishId && !newFinishes.includes(targetFinishId)) newFinishes.push(targetFinishId);
        const prevRelevant = prev.filter(id => !ALL_BINDING_FINISH_IDS.includes(id) || (targetFinishId && id === targetFinishId));
        if (JSON.stringify(newFinishes.slice().sort()) !== JSON.stringify(prevRelevant.slice().sort())) return newFinishes;
        return prev;
      });
    } else {
      setSelectedFinishes(prev => {
        const newFinishes = prev.filter(id => !ALL_BINDING_FINISH_IDS.includes(id));
        if (JSON.stringify(newFinishes.slice().sort()) !== JSON.stringify(prev.slice().sort())) return newFinishes;
        return prev;
      });
    }
  }, [isBooklet, bindingType, isLoadingQuote, isSettingFromQuoteInternal]);

  // Sync cover sides with main sides only when separate cover is first enabled
  useEffect(() => {
    if (separateCover && !isLoadingQuote && !isSettingFromQuoteInternal) {
      setCoverIsDoubleSided(isDoubleSided);
    }
  }, [separateCover, isLoadingQuote, isSettingFromQuoteInternal]); // Removed isDoubleSided from dependencies

  // Auto-initialize sheetPrinters when sheetsPerSet changes (default to mono)
  useEffect(() => {
    if (isSettingFromQuoteInternal) {
      return; // Don't override when loading from quote
    }

    if (sheetPrinters.length !== sheetsPerSet) {
      setSheetPrinters(Array(sheetsPerSet).fill('mono')); // Default to Black and White (mono)
    }
  }, [sheetsPerSet, sheetPrinters.length, setSheetPrinters, isSettingFromQuoteInternal]);

  // Auto-initialize sheetSides when sheetsPerSet changes (default to 2 = double-sided)
  useEffect(() => {
    if (isSettingFromQuoteInternal) {
      return; // Don't override when loading from quote
    }

    if (sheetSides.length !== sheetsPerSet) {
      setSheetSides(Array(sheetsPerSet).fill(1)); // Default to Single-sided (1) for NCR jobs
    }
  }, [sheetsPerSet, sheetSides.length, setSheetSides, isSettingFromQuoteInternal]);

  const toggleFinishingProcess = (processId) => {
    setSelectedFinishes(prev => {
      const isSelected = prev.includes(processId);
      let updated;
      if (isSelected) {
        updated = prev.filter(id => id !== processId);
        if (isBooklet && BINDING_TYPE_TO_FINISH_ID_MAP[bindingType] === processId) setBindingType('');
      } else {
        updated = [...prev, processId];
        if (isBooklet && ALL_BINDING_FINISH_IDS.includes(processId)) {
          const newBinding = Object.keys(BINDING_TYPE_TO_FINISH_ID_MAP).find(key => BINDING_TYPE_TO_FINISH_ID_MAP[key] === processId);
          if (newBinding && newBinding !== bindingType) setBindingType(newBinding);
        }

        // Initialize default finishing options when a process is added
        if (processId === 'wirebind') {
          setFinishingOptions(prevOptions => {
            // Only set default if wirebind options don't exist or bindingEdge is not set
            if (!prevOptions.wirebind || !prevOptions.wirebind.bindingEdge) {
              return {
                ...prevOptions,
                wirebind: {
                  ...prevOptions.wirebind,
                  bindingEdge: 'left' // Default binding edge
                }
              };
            }
            return prevOptions;
          });
        }
      }
      if ((processId === FOLD_ID || processId === CREASE_ID) && !updated.includes(processId)) {
        if (!updated.includes(FOLD_ID) && !updated.includes(CREASE_ID)) setFoldType('');
      }
      return updated;
    });
  };

  // New function to handle binding type changes and sync job type
  const handleBindingTypeChange = useCallback((newBindingType) => {
    setBindingType(newBindingType);

    const newJobType = BINDING_TYPE_TO_JOB_TYPE_MAP[newBindingType];
    if (newJobType) {
      isInternalJobTypeChangeRef.current = true; // Set flag to prevent suggestion load
      setJobType(newJobType);
    }
  }, []);

  const toggleCoverFinishingProcess = (processId) => {
    setSelectedCoverFinishes(prev => prev.includes(processId) ? prev.filter(id => id !== processId) : [...prev, processId]);
  };

  const toggleMiscCost = (costId) => {
    setSelectedMiscCosts(prev => prev.includes(costId) ? prev.filter(id => id !== costId) : [...prev, costId]);
  };

  const loadJobSuggestion = useCallback(async (selectedJobType) => {
    if (!selectedJobType || isLoadingQuote || isSettingFromQuoteInternal || (jobTypeLoadedFromQuoteRef.current === selectedJobType && !isLoadingQuote) || isInternalJobTypeChangeRef.current) {
      return;
    }
    if (isInternalJobTypeChangeRef.current) {
      return; // Explicitly skip if it's an internal change
    }

    try {
      const response = await fetch(`${API_URL}/api/small-format/job-suggestion?job_type=${selectedJobType}`);
      if (!response.ok) throw new Error('Failed to get job suggestion');
      const rawSuggestion = await response.json();
      const suggestion = toCamelCase(rawSuggestion);

      console.log('Using suggestion (camelCased):', suggestion);

      const setIfNotLoaded = (field, setter, value, defaultValue) => {
        // Don't override if field was loaded from quote OR user has manually modified it
        if (!fieldsLoadedFromQuote.has(field) && !userModifiedFields.has(field)) {
          setter(value === undefined ? defaultValue : value);
        }
      };

      setIfNotLoaded('paperStock', setPaperStock, suggestion.paperStock, '');
      setIfNotLoaded('printer', setPrinter, suggestion.printer, '');

      // Sync colours state with printer selection
      let coloursSynced = false;
      if (!fieldsLoadedFromQuote.has('printer') && suggestion.printer) {
        if (suggestion.printer === 'mono') {
          setColours('black_white');
          coloursSynced = true;
        } else if (suggestion.printer === 'colour') {
          setColours('full_colour');
          coloursSynced = true;
        }
      }

      setIfNotLoaded('standardSize', setStandardSize, suggestion.standardSize, '');
      setIfNotLoaded('width', setWidth, suggestion.width, 0);
      setIfNotLoaded('height', setHeight, suggestion.height, 0);
      setIfNotLoaded('quantity', setQuantity, suggestion.quantity, 1);
      setIfNotLoaded('isDoubleSided', setIsDoubleSided, suggestion.doubleSided, true);
      setIfNotLoaded('finishes', setSelectedFinishes, suggestion.finishing, []);
      setIfNotLoaded('finishingOptions', setFinishingOptions, suggestion.finishingOptions, {});
      setIfNotLoaded('miscCosts', setSelectedMiscCosts, suggestion.miscCosts, []);
      setIfNotLoaded('bleed', setBleed, suggestion.bleed, 3);

      // Only set colours from suggestion if we haven't already synced them with the printer
      if (!coloursSynced && !fieldsLoadedFromQuote.has('colours')) {
        setColours(suggestion.colours === undefined ? 'full_colour' : suggestion.colours);
      }

      const isActuallyBookletType = BOOKLET_JOB_TYPES.includes(selectedJobType);
      const isCustomType = selectedJobType === CUSTOM_JOB_TYPE_ID;

      if (isActuallyBookletType || isCustomType) {
        setIfNotLoaded('pageCount', setPageCount, suggestion.pageCount, 16);
        setIfNotLoaded('bindingType', setBindingType, suggestion.bindingType, 'saddle-stitch');
        setIfNotLoaded('separateCover', setSeparateCover, suggestion.separateCover, false);
        setIfNotLoaded('coverStock', setCoverStock, suggestion.coverStock, '');
        setIfNotLoaded('selectedCoverFinishes', setSelectedCoverFinishes, suggestion.selectedCoverFinishes, []);
        setIfNotLoaded('coverColours', setCoverColours, suggestion.coverColours, 'full_colour');
        setIfNotLoaded('coverFinishingOptions', setCoverFinishingOptions, suggestion.coverFinishingOptions, {});
        if (isCustomType && suggestion.isBooklet !== undefined && !fieldsLoadedFromQuote.has('isBooklet')) {
          setIsBooklet(suggestion.isBooklet);
        }
      } else {
        // Reset booklet fields if not a booklet type and not loaded from quote
        if (!fieldsLoadedFromQuote.has('pageCount')) setPageCount(16);
        if (!fieldsLoadedFromQuote.has('bindingType')) setBindingType('saddle-stitch');
        if (!fieldsLoadedFromQuote.has('separateCover')) setSeparateCover(false);
        if (!fieldsLoadedFromQuote.has('coverStock')) setCoverStock('');
        if (!fieldsLoadedFromQuote.has('selectedCoverFinishes')) setSelectedCoverFinishes([]);
        if (!fieldsLoadedFromQuote.has('coverFinishingOptions')) setCoverFinishingOptions({});
        if (!fieldsLoadedFromQuote.has('coverColours')) setCoverColours('full_colour');
      }

      if (selectedJobType === 'ncr') {
        setIfNotLoaded('ncrType', setNcrType, suggestion.ncrType, 'sets');
        setIfNotLoaded('sheetsPerSet', setSheetsPerSet, suggestion.sheetsPerSet, 2);
        setIfNotLoaded('setsPerPad', setSetsPerPad, suggestion.setsPerPad, 50);
        if (!fieldsLoadedFromQuote.has('paperSelections')) setPaperSelections([]); // Special handling for NCR paper
        setIfNotLoaded('padGlueEdge', setPadGlueEdge, suggestion.padGlueEdge, 'top');
        if (!fieldsLoadedFromQuote.has('isDoubleSided')) setIsDoubleSided(false);
        if (!fieldsLoadedFromQuote.has('bleed')) setBleed(0);
        if (!fieldsLoadedFromQuote.has('paperStock') && suggestion.paperStock) {
          // NCR uses paperSelections, not paperStock directly from suggestion
        }
        if (!fieldsLoadedFromQuote.has('paperStock')) setPaperStock(''); // Ensure paperStock is cleared for NCR
      }
      if (selectedJobType === 'pads') { // Assuming 'pads' is the ID for the new job type
        setIfNotLoaded('padJobSheetsPerPad', setPadJobSheetsPerPad, suggestion.padJobSheetsPerPad, 50);
        setIfNotLoaded('padGlueEdge', setPadGlueEdge, suggestion.padGlueEdge, 'top'); // Reuses existing padGlueEdge
      }

    } catch (err) {
      console.error('Error loading job suggestion:', err);
    }
  }, [API_URL, printers, isLoadingQuote, jobTypeLoadedFromQuoteRef, fieldsLoadedFromQuote, isSettingFromQuoteInternal, userModifiedFields]);

  useEffect(() => {
    // Modified guard to check the new ref
    if (!jobType || isLoadingQuote || isSettingFromQuoteInternal || jobTypeLoadedFromQuoteRef.current === jobType || isInternalJobTypeChangeRef.current) {
      if (isInternalJobTypeChangeRef.current) {
        isInternalJobTypeChangeRef.current = false; // Reset the flag after skipping
      }
      return;
    }
    loadJobSuggestion(jobType);
  }, [jobType, isLoadingQuote, isSettingFromQuoteInternal]); // Remove loadJobSuggestion and jobTypeLoadedFromQuoteRef from dependencies

  const calculatePrice = useCallback(async () => {
    setCalculationError(null);
    setDetailsError(null);

    const baseChecksFail = (!jobType || !printer || quantity <= 0);
    const ncrPaperCheckFail = jobType === 'ncr' && (!paperSelections || paperSelections.length === 0 || paperSelections.length < sheetsPerSet || paperSelections.some(s => !s));
    const standardPaperCheckFail = jobType !== 'ncr' && !paperStock;
    const dimensionCheckFail = !isBooklet && (width <= 0 || height <= 0);
    const bookletPageCheckFail = isBooklet && pageCount <= 0;

    if (baseChecksFail || standardPaperCheckFail || dimensionCheckFail || bookletPageCheckFail) {
      setCalculatedPrice(0); setBreakdown({}); setImpositionDetails(null);
      setBookletDetails(null); setFoldingDetails(null); setPaperUsage(null);
      if (ncrPaperCheckFail && jobType === 'ncr') {
        const missingParts = sheetsPerSet - paperSelections.filter(s => s).length;
        setCalculationError(`Please select paper for all ${sheetsPerSet} NCR parts. ${missingParts > 0 ? `${missingParts} part(s) missing.` : ''}`);
      }
      return;
    }
    if (ncrPaperCheckFail && jobType === 'ncr') { // Separate check for NCR paper error message
      const missingParts = sheetsPerSet - paperSelections.filter(s => s).length;
      setCalculationError(`Please select paper for all ${sheetsPerSet} NCR parts. ${missingParts > 0 ? `${missingParts} part(s) missing.` : ''}`);
      setCalculatedPrice(0); setBreakdown({}); setImpositionDetails(null);
      setBookletDetails(null); setFoldingDetails(null); setPaperUsage(null);
      return;
    }

    const userPricingTier = pricingTier || currentUser?.pricing_tier || 'retail';
    let widthForBackend = width, heightForBackend = height;
    let finishedWidthForBackend = width, finishedHeightForBackend = height;

    if (isFolded && !isBooklet && foldType) {
      if (dimensionInputType === 'finished') {
        const unfolded = calculateUnfoldedDimensions(width, height, foldType);
        widthForBackend = unfolded.unfoldedWidthMm; heightForBackend = unfolded.unfoldedHeightMm;
      } else {
        const folded = calculateFoldedDimensions(width, height, foldType);
        finishedWidthForBackend = folded.width; finishedHeightForBackend = folded.height;
      }
    }

    const jobData = {
      jobType,
      paperStock: jobType === 'ncr' ? '' : paperStock,
      printer, width: widthForBackend, height: heightForBackend,
      finishedWidthMm: finishedWidthForBackend, finishedHeightMm: finishedHeightForBackend,
      dimensionInputType: isFolded && !isBooklet ? dimensionInputType : null,
      quantity, doubleSided: isDoubleSided, colours, finishing: selectedFinishes,
      finishingOptions: finishingOptions, miscCosts: selectedMiscCosts, bleed, numberOfDesigns, standardSize,
      isBooklet, bindingType: isBooklet ? bindingType : null, pageCount,
      coverStock, separateCover, foldType,
      coverColours: isBooklet && separateCover ? coverColours : null,
      coverIsDoubleSided: isBooklet && separateCover ? coverIsDoubleSided : null,
      selectedCoverFinishes: isBooklet && separateCover ? selectedCoverFinishes : [],
      coverFinishingOptions: isBooklet && separateCover ? coverFinishingOptions : {},
      ncrType: jobType === 'ncr' ? ncrType : undefined,
      sheetsPerSet: jobType === 'ncr' ? sheetsPerSet : undefined,
      setsPerPad: jobType === 'ncr' && ncrType === 'pads' ? setsPerPad : undefined,
      paperSelections: jobType === 'ncr' ? paperSelections : undefined,
      sheetPrinters: jobType === 'ncr' ? sheetPrinters : undefined,
      sheetSides: jobType === 'ncr' ? sheetSides : undefined,
      hasWraparoundShield: jobType === 'ncr' && ncrType === 'pads' ? hasWraparoundShield : undefined,
      hasLooseShield: jobType === 'ncr' && ncrType === 'pads' ? hasLooseShield : undefined,
      padGlueEdge: jobType === 'ncr' && ncrType === 'pads' ? padGlueEdge : undefined,
      hasNumbering: jobType === 'ncr' ? hasNumbering : undefined,
      numberingStartAt: jobType === 'ncr' && hasNumbering ? numberingStartAt : undefined,
      padJobSheetsPerPad: jobType === 'pads' ? padJobSheetsPerPad : undefined,
      padJobGlueEdge: jobType === 'pads' ? padGlueEdge : undefined, // Send glue edge for 'pads' type
      pricingTier: userPricingTier,
      ...(selectedCustomer?.customer_id ? { customerId: selectedCustomer.customer_id } : {}),
    };

    try {
      const response = await fetch(`${API_URL}/api/small-format/calculate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(jobData),
      });
      if (!response.ok) { const errTxt = await response.text(); throw new Error(`Calc API (${response.status}): ${errTxt}`); }
      const priceData = await response.json();
      setCalculatedPrice(priceData.calculatedPrice);
      setBreakdown(priceData.breakdown);
      setImpositionDetails(priceData.breakdown.inner_page_imposition_details || priceData.breakdown.imposition || null);
      setBookletDetails(priceData.breakdown.booklet || null);
      if (!priceData.breakdown.inner_page_imposition_details && !priceData.breakdown.imposition) setImpositionDetails(null);
      if (!priceData.breakdown.booklet) setBookletDetails(null);

    } catch (err) {
      console.error('Error during main price calculation:', err);
      setCalculationError(`Calculation Error: ${err.message}`);
      setCalculatedPrice(0); setBreakdown({}); setImpositionDetails(null);
      setBookletDetails(null); setFoldingDetails(null); setPaperUsage(null);
      return;
    }

    try {
      const usageRes = await fetch(`${API_URL}/api/small-format/paper-usage`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(jobData),
      });
      if (usageRes.ok) setPaperUsage(await usageRes.json());
      else { const errTxt = await usageRes.text(); setDetailsError(p => (p ? `${p}; ` : '') + `Paper Usage API (${usageRes.status}): ${errTxt}`); setPaperUsage(null); }
    } catch (error) { setDetailsError(p => (p ? `${p}; ` : '') + 'Error loading paper usage.'); setPaperUsage(null); }

    if (foldType) {
      try {
        const foldRes = await fetch(`${API_URL}/api/small-format/folding`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ width: widthForBackend, height: heightForBackend, folds: [foldType] }),
        });
        if (foldRes.ok) setFoldingDetails(await foldRes.json());
        else { const errTxt = await foldRes.text(); setDetailsError(p => (p ? `${p}; ` : '') + `Folding API (${foldRes.status}): ${errTxt}`); setFoldingDetails(null); }
      } catch (error) { setDetailsError(p => (p ? `${p}; ` : '') + 'Error loading folding details.'); setFoldingDetails(null); }
    } else {
      setFoldingDetails(null);
    }
  }, [
    jobType, paperStock, printer, width, height, quantity,
    isDoubleSided, colours, selectedFinishes, selectedMiscCosts,
    bleed, numberOfDesigns, standardSize, isBooklet, pageCount,
    coverStock, separateCover, foldType, API_URL, bindingType, finishingOptions,
    selectedCoverFinishes, coverFinishingOptions, coverColours, currentUser,
    ncrType, sheetsPerSet, setsPerPad, paperSelections, sheetPrinters, sheetSides,
    hasWraparoundShield, hasLooseShield, padGlueEdge, hasNumbering, numberingStartAt,
    dimensionInputType, isFolded, padJobSheetsPerPad, pricingTier
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculatePrice();
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [
    jobType, paperStock, printer, width, height, quantity,
    isDoubleSided, colours, selectedFinishes, selectedMiscCosts,
    bleed, numberOfDesigns, standardSize, isBooklet, pageCount,
    coverStock, separateCover, foldType, bindingType, finishingOptions,
    selectedCoverFinishes, coverFinishingOptions, coverColours, coverIsDoubleSided,
    ncrType, sheetsPerSet, setsPerPad, paperSelections, sheetPrinters, sheetSides,
    hasWraparoundShield, hasLooseShield, padGlueEdge, hasNumbering, numberingStartAt,
    dimensionInputType, isFolded, padJobSheetsPerPad, pricingTier
  ]);

  const handleStandardSizeChange = (sizeName) => {
    setStandardSize(sizeName);
    if (sizeName && paperSizes[sizeName]) {
      const size = paperSizes[sizeName];
      setWidth(size.width_mm);
      setHeight(size.height_mm);
    }
  };

  const getLaminateTypeName = (laminateTypeId) => {
    switch (laminateTypeId) {
      case 'matt_lam': return 'Matt';
      case 'gloss_lam': return 'Gloss';
      case 'soft_touch_lam': return 'Soft Touch';
      default: return '';
    }
  };

  const getFinishingMaterialDisplayName = (processId, materialId, allFinishingMaterials) => {
    if (!materialId) return '';
    if (processId === 'laminate' || processId === 'duplex-laminate') return getLaminateTypeName(materialId);

    const processSpecificMaterials = allFinishingMaterials[processId] || [];
    const foundMaterial = processSpecificMaterials.find(m => m.material_id === materialId);
    if (foundMaterial?.name) return foundMaterial.name;

    if (processId === 'encapsulate') {
      const defaults = [{ material_id: '250matt', name: 'Matt (250 mic)' }, { material_id: '250gloss', name: 'Gloss (250 mic)' }];
      const defaultMat = defaults.find(m => m.material_id === materialId);
      if (defaultMat) return defaultMat.name;
    }
    return materialId;
  };

  const createFullProductObject = () => {
    if (calculatedPrice <= 0) return null;

    const jobTypeName = jobTypes.find(jt => jt.id === jobType)?.name || '';
    const selectedPaper = paperStocks.find(s => s.id === paperStock);
    const paperStockName = selectedPaper?.name || '';
    let coverStockName;
    if (isBooklet && separateCover && coverStock) {
      coverStockName = paperStocks.find(s => s.id === coverStock)?.name || coverStock;
    }

    let ncrDesc = '', paperSelNames = [];
    if (jobType === 'ncr') {
      paperSelNames = paperSelections.map(id => paperStocks.find(s => s.id === id)?.name || '').filter(Boolean);
      ncrDesc = ncrType === 'sets' ? `${sheetsPerSet}-part ${standardSize || `${width}x${height}mm`} sets`
        : `${setsPerPad} sets of ${sheetsPerSet}-part ${standardSize || `${width}x${height}mm`} per pad`;
      const extras = [];
      if (hasWraparoundShield) extras.push('wraparound shield');
      if (hasLooseShield) extras.push('loose shield');
      if (extras.length > 0) ncrDesc += ` with ${extras.join(' and ')}`;
    }

    const mapFinishes = (finishIds, options, materials) =>
      finishIds.map(id => {
        const process = finishingProcesses.find(p => p.id === id);
        if (!process) return '';
        const optKey = (id === 'laminate' || id === 'duplex-laminate') ? 'laminateType' : 'material_type';
        const matId = options[id]?.[optKey];
        const matName = matId ? getFinishingMaterialDisplayName(id, matId, materials) : '';
        return matName ? `${process.name} (${matName})` : process.name;
      }).filter(Boolean);

    const finishingNames = mapFinishes(selectedFinishes, finishingOptions, finishingMaterials);
    const coverFinishingNames = mapFinishes(selectedCoverFinishes, coverFinishingOptions, finishingMaterials);

    return {
      id: 'temp_' + Math.random().toString(36).substring(2, 15),
      productType: 'small_format',
      jobName: jobType === 'ncr'
        ? (jobName || `NCR ${ncrType} ${sheetsPerSet}-part ${standardSize || `${width}x${height}mm`}`)
        : (jobName || `${jobTypeName} ${width}mm x ${height}mm`),
      materialNames: jobType === 'ncr' ? paperSelNames.join(', ') : paperStockName,
      material: jobType === 'ncr' ? paperSelNames.join(', ') : paperStockName,
      dimensions: isBooklet ? `${width}mm x ${height}mm (${pageCount} pages)` : `${width}mm x ${height}mm`,
      quantity,
      processes: finishingNames,
      coverProcesses: coverFinishingNames,
      price: calculatedPrice,
      breakdown, width, height,
      finishes: selectedFinishes, finishingOptions: finishingOptions,
      miscCosts: selectedMiscCosts, printerId: printer, jobType: jobTypeName,
      isDoubleSided, colours, notes, isBooklet,
      pageCount: isBooklet ? pageCount : undefined,
      paperStock, paperStockName,
      coverStock: isBooklet && separateCover ? coverStock : undefined, coverStockName,
      bleed, numberOfDesigns, standardSize,
      selectedCoverFinishes: isBooklet && separateCover ? selectedCoverFinishes : undefined,
      coverFinishingOptions: isBooklet && separateCover ? coverFinishingOptions : undefined,
      coverColours: isBooklet && separateCover ? coverColours : undefined,
      coverIsDoubleSided: isBooklet && separateCover ? coverIsDoubleSided : undefined,
      dimension_input_type: isFolded && !isBooklet ? dimensionInputType : null,
      foldType,
      addedAt: new Date().toISOString(),
      ncrType: jobType === 'ncr' ? ncrType : undefined,
      sheetsPerSet: jobType === 'ncr' ? sheetsPerSet : undefined,
      setsPerPad: jobType === 'ncr' && ncrType === 'pads' ? setsPerPad : undefined,
      paperSelections: jobType === 'ncr' ? paperSelections : undefined,
      paperSelectionNames: jobType === 'ncr' ? paperSelNames : undefined,
      sheetPrinters: jobType === 'ncr' ? sheetPrinters : undefined,
      sheetSides: jobType === 'ncr' ? sheetSides : undefined,
      hasWraparoundShield: jobType === 'ncr' && ncrType === 'pads' ? hasWraparoundShield : undefined,
      hasLooseShield: jobType === 'ncr' && ncrType === 'pads' ? hasLooseShield : undefined,
      padGlueEdge: jobType === 'ncr' && ncrType === 'pads' ? padGlueEdge : undefined,
      padJobSheetsPerPad: jobType === 'pads' ? padJobSheetsPerPad : undefined,
      // ncrDescription is specific to NCR, so no change here for 'pads'
      hasNumbering: jobType === 'ncr' ? hasNumbering : undefined,
      numberingStartAt: jobType === 'ncr' && hasNumbering ? numberingStartAt : undefined,
      ncrDescription: jobType === 'ncr' ? ncrDesc : undefined,
    };
  };

  // Helper function to mark a field as user-modified
  const markFieldAsUserModified = (fieldName) => {
    setUserModifiedFields(prev => new Set([...prev, fieldName]));
  };

  const resetForm = () => {
    setJobType(''); setPaperStock(''); setWidth(0); setHeight(0); setQuantity(1);
    setSelectedFinishes([]); setSelectedMiscCosts([]); setStandardSize('');
    setIsDoubleSided(true); setColours('full_colour'); setNotes(''); setBleed(3);
    setNumberOfDesigns(1); setJobName(''); setIsBooklet(false); setPageCount(16); setCoverStock('');
    setSeparateCover(false); setCoverColours('full_colour'); setCoverIsDoubleSided(true); setSelectedCoverFinishes([]);
    setCoverFinishingOptions({}); setFoldType(''); setCalculatedPrice(0);
    setBreakdown({}); setImpositionDetails(null); setBookletDetails(null);
    setFoldingDetails(null); setPaperUsage(null);
    setBindingType('saddle-stitch'); setFinishingOptions({});
    setNcrType('sets'); setSheetsPerSet(2); setSetsPerPad(50); setPaperSelections([]);
    setHasWraparoundShield(false); setPadGlueEdge('top'); setHasNumbering(false);
    setNumberingStartAt(1); setHasLooseShield(false); setDimensionInputType('finished');
    setPadJobSheetsPerPad(50); // Reset new state for 'pads'
    setUserModifiedFields(new Set()); // Clear user-modified fields on reset
    // Note: activeTab is managed by the component, not this hook
    // window.scrollTo(0, 0); // Also component's responsibility
  };

  // Return all state and functions needed by the component
  return {
    // State
    jobType, paperStock, width, height, quantity, selectedFinishes, selectedMiscCosts,
    printer, standardSize, isDoubleSided, colours, notes, bleed, numberOfDesigns, jobName, finishingOptions,
    isBooklet, pageCount, coverStock, separateCover, coverColours, coverIsDoubleSided, bindingType,
    selectedCoverFinishes, coverFinishingOptions,
    foldType, isFolded, dimensionInputType,
    ncrType, sheetsPerSet, setsPerPad, paperSelections, hasWraparoundShield,
    padGlueEdge, hasNumbering, numberingStartAt, hasLooseShield, sheetPrinters, sheetSides,
    padJobSheetsPerPad, // New state for 'pads'
    calculatedPrice, breakdown, impositionDetails, bookletDetails, foldingDetails, paperUsage,
    calculationError, detailsError,

    // Setters
    setJobType, setPaperStock, setWidth, setHeight, setQuantity, setSelectedFinishes,
    setSelectedMiscCosts, setPrinter, setStandardSize, setIsDoubleSided, setColours,
    setNotes, setBleed, setNumberOfDesigns, setJobName, setFinishingOptions,
    setIsBooklet, setPageCount, setCoverStock, setSeparateCover, setCoverColours,
    setCoverIsDoubleSided, setBindingType, setSelectedCoverFinishes, setCoverFinishingOptions,
    setFoldType, setIsFolded, setDimensionInputType,
    setNcrType, setSheetsPerSet, setSetsPerPad, setPaperSelections, setHasWraparoundShield,
    setPadGlueEdge, setHasNumbering, setNumberingStartAt, setHasLooseShield, setSheetPrinters, setSheetSides,
    setPadJobSheetsPerPad, // New setter for 'pads'
    setCalculatedPrice, setBreakdown, setImpositionDetails, setBookletDetails,
    setIsSettingFromQuoteInternal, // Expose setter for internal state
    setFoldingDetails, setPaperUsage, setCalculationError, setDetailsError,

    // Functions
    calculatePrice, // Though mostly used internally via useEffect
    loadJobSuggestion,
    resetForm,
    createFullProductObject,
    toggleFinishingProcess,
    handleBindingTypeChange, // Expose the new handler
    toggleCoverFinishingProcess,
    toggleMiscCost,
    handleStandardSizeChange,
    handleOptionChange,
    handleCheckboxChange,
    handleCoverOptionChange,
    handleCoverCheckboxChange,
    getFinishingMaterialDisplayName, // Expose if needed in UI for summary
    markFieldAsUserModified, // Helper to track user modifications
  };
};
